import React from 'react';
import { AlertCircle } from 'lucide-react';
import { PriceIncreaseItem } from '../../../api/types';

export interface DocumentPriceIncreases {
    /** Display name for the document (e.g. filename) */
    documentName: string;
    /** Vendor from the invoice/bill */
    vendorName: string;
    /** Invoice/bill number if available */
    invoiceNumber?: string;
    /** Line items with price increases for this document */
    items: PriceIncreaseItem[];
    /** Line items with price decreases for this document */
    decreaseItems?: PriceIncreaseItem[];
}

interface BatchPriceIncreaseModalProps {
    /** Groups of price increases, one per document (invoice/bill) */
    documents: DocumentPriceIncreases[];
    onClose: () => void;
}

const BatchPriceIncreaseModal: React.FC<BatchPriceIncreaseModalProps> = ({ documents, onClose }) => {
    const totalIncreases = documents.reduce((sum, d) => sum + d.items.length, 0);
    const totalDecreases = documents.reduce((sum, d) => sum + (d.decreaseItems?.length ?? 0), 0);
    const hasBoth = totalIncreases > 0 && totalDecreases > 0;

    return (
        <div className="fixed inset-0 z-[3001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-6">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3 text-amber-700">
                        <AlertCircle className="w-8 h-8 shrink-0" />
                        <div>
                            <h3 className="text-lg font-semibold">
                                {hasBoth ? 'Price changes detected' : totalIncreases > 0 ? 'Price increases detected' : 'Price decreases detected'}
                            </h3>
                            <p className="text-sm text-gray-600 mt-0.5">
                                {totalIncreases > 0 && (
                                    <>{totalIncreases} line item{totalIncreases !== 1 ? 's' : ''} had unit price increases.</>
                                )}
                                {hasBoth && ' '}
                                {totalDecreases > 0 && (
                                    <>{totalDecreases} line item{totalDecreases !== 1 ? 's' : ''} had unit price decreases.</>
                                )}
                                {' '}Across {documents.length} document{documents.length !== 1 ? 's' : ''}.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="p-6 overflow-y-auto flex-1">
                    <div className="space-y-6">
                        {documents.map((doc, docIdx) => (
                            <div key={docIdx} className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                    <p className="font-medium text-gray-900">{doc.documentName}</p>
                                    <p className="text-sm text-gray-600 mt-0.5">
                                        {doc.vendorName}
                                        {doc.invoiceNumber && (
                                            <span className="ml-2">• {doc.invoiceNumber}</span>
                                        )}
                                    </p>
                                </div>
                                {doc.items.length > 0 && (
                                    <>
                                        <div className="px-4 py-2 bg-amber-50/50 border-b border-gray-100 text-xs font-medium text-amber-800">
                                            Price increases
                                        </div>
                                        <ul className="divide-y divide-gray-100">
                                            {doc.items.map((item, itemIdx) => (
                                                <li key={`inc-${itemIdx}`} className="px-4 py-3 bg-amber-50/30">
                                                    <p className="font-medium text-gray-900">{item.description || 'Line item'}</p>
                                                    <p className="text-sm mt-2">
                                                        <span className="text-gray-500">Previous:</span>{' '}
                                                        <span className="line-through">
                                                            {item.currency} {item.previous_most_recent_unit_price.toFixed(2)}
                                                        </span>
                                                        {' → '}
                                                        <span className="font-medium text-amber-700">
                                                            {item.currency} {item.unit_price.toFixed(2)}
                                                        </span>
                                                    </p>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                                {(doc.decreaseItems?.length ?? 0) > 0 && (
                                    <>
                                        <div className="px-4 py-2 bg-green-50/50 border-b border-gray-100 text-xs font-medium text-green-800">
                                            Price decreases
                                        </div>
                                        <ul className="divide-y divide-gray-100">
                                            {(doc.decreaseItems ?? []).map((item, itemIdx) => (
                                                <li key={`dec-${itemIdx}`} className="px-4 py-3 bg-green-50/30">
                                                    <p className="font-medium text-gray-900">{item.description || 'Line item'}</p>
                                                    <p className="text-sm mt-2">
                                                        <span className="text-gray-500">Previous:</span>{' '}
                                                        <span className="line-through">
                                                            {item.currency} {item.previous_most_recent_unit_price.toFixed(2)}
                                                        </span>
                                                        {' → '}
                                                        <span className="font-medium text-green-700">
                                                            {item.currency} {item.unit_price.toFixed(2)}
                                                        </span>
                                                    </p>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="p-6 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="bg-blue-600 text-white font-medium py-2.5 px-6 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Got it
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BatchPriceIncreaseModal;
